/**
 * MVA tracker: who is working on which prospect's Market Value Analysis.
 *
 * Who holds an MVA and how far along it is are decisions a person makes, so
 * they are stored (mva_assignments, one open row per company). How many each
 * person is carrying, and whether one is late, are derived here at read time
 * and never stored.
 *
 * Delivered is the only closed status. Marking one Delivered is a stage-floor
 * signal (shared/stageFloor.ts, "mva_delivered"), so the prospect moves to
 * "MVA delivered" without a second click.
 */

export const MVA_STATUSES = ["Not started", "Waiting on financials", "In progress", "In review", "Delivered"] as const;
export type MvaStatus = (typeof MVA_STATUSES)[number];
export const MVA_DELIVERED = "Delivered" satisfies MvaStatus;
export const MVA_OPEN_STATUSES: readonly MvaStatus[] = MVA_STATUSES.filter((s) => s !== MVA_DELIVERED);

export function isMvaStatus(s: unknown): s is MvaStatus {
  return typeof s === "string" && (MVA_STATUSES as readonly string[]).includes(s);
}

export function isOpenMva(status: string | null | undefined): boolean {
  return isMvaStatus(status) && status !== MVA_DELIVERED;
}

export interface MvaFields {
  companyId?: string;
  assignedTo?: string | null;
  status?: MvaStatus;
  dueOn?: string | null;
  notes?: string | null;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NOTES_MAX = 2000;

function isRealDate(s: string): boolean {
  const m = ISO_DATE.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

/**
 * Validate a create or edit body. Only keys present in the body come back, so
 * a PATCH that changes the status cannot clear the due date by omission.
 */
export function parseMvaFields(
  body: unknown,
  opts: { create: boolean },
): { ok: true; value: MvaFields } | { ok: false; error: string } {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const out: MvaFields = {};

  if (opts.create) {
    if (typeof b.companyId !== "string" || !b.companyId.trim()) return { ok: false, error: "companyId is required" };
    out.companyId = b.companyId.trim();
  }
  if ("assignedTo" in b) {
    if (b.assignedTo !== null && typeof b.assignedTo !== "string") return { ok: false, error: "assignedTo must be a user id or null" };
    out.assignedTo = typeof b.assignedTo === "string" && b.assignedTo.trim() ? b.assignedTo.trim() : null;
  }
  if ("status" in b) {
    if (!isMvaStatus(b.status)) return { ok: false, error: `status must be one of: ${MVA_STATUSES.join(", ")}` };
    out.status = b.status;
  }
  if ("dueOn" in b) {
    if (b.dueOn === null || b.dueOn === "") out.dueOn = null;
    else if (typeof b.dueOn === "string" && isRealDate(b.dueOn)) out.dueOn = b.dueOn;
    else return { ok: false, error: "dueOn must be YYYY-MM-DD" };
  }
  if ("notes" in b) {
    if (b.notes !== null && typeof b.notes !== "string") return { ok: false, error: "notes must be text" };
    const n = typeof b.notes === "string" ? b.notes.trim() : "";
    if (n.length > NOTES_MAX) return { ok: false, error: `notes must be under ${NOTES_MAX} characters` };
    out.notes = n || null;
  }

  if (!opts.create && Object.keys(out).length === 0) return { ok: false, error: "Nothing to update" };
  return { ok: true, value: out };
}

/** The viewer's calendar day as YYYY-MM-DD. `toISOString` would give the UTC day. */
export function localIsoDate(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Open and past its due date. Due today is not late yet. */
export function isMvaOverdue(m: { status: string; dueOn: string | null }, today: string): boolean {
  return isOpenMva(m.status) && !!m.dueOn && m.dueOn < today;
}

export interface MvaTallyRow { userId: string | null; name: string; open: number; overdue: number }

/** Open MVAs per person, busiest first, Unassigned last. Delivered ones are not counted. */
export function tallyByAssignee(
  rows: { assignedTo: string | null; assigneeName: string | null; status: string; dueOn: string | null }[],
  today: string,
): MvaTallyRow[] {
  const byUser = new Map<string, MvaTallyRow>();
  for (const r of rows) {
    if (!isOpenMva(r.status)) continue;
    const key = r.assignedTo ?? "";
    const t = byUser.get(key) ?? { userId: r.assignedTo, name: r.assignedTo ? r.assigneeName ?? "Unknown" : "Unassigned", open: 0, overdue: 0 };
    t.open++;
    if (isMvaOverdue(r, today)) t.overdue++;
    byUser.set(key, t);
  }
  return [...byUser.values()].sort((a, b) => {
    if (!a.userId !== !b.userId) return a.userId ? -1 : 1;
    return b.open - a.open || a.name.localeCompare(b.name);
  });
}
